<script setup>
// 图片编码工具（free-tools）。复刻 design 两个单页的 inline JS：
//   ascii  → "Image to ASCII"：上传图片 → canvas 采样亮度 → 字符画文本(可调宽度/字符集/反色) → 复制/下载 .txt
//   base64 → "Image to Base64"：上传图片 → FileReader.readAsDataURL → data URI / raw / CSS background → 复制
// 纯浏览器(Canvas / FileReader)，文件不上传。dropzone → workspace → 文本输出 + 复制/下载，沿用 AiLandingTool 结构。
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { fileTooLargeMsg } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  mode: { type: String, default: "ascii" }, // ascii | base64
  slug: { type: String, default: "" },
});

const isAscii = computed(() => props.mode === "ascii");

/* ---------- 共享状态 ---------- */
const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileInput = ref(null);
const fileName = ref("");
const fileSize = ref(0);
const thumbUrl = ref("");
const copied = ref(false);
let lastUrl = "";

const HEAD = {
  ascii: { title: "Drop your image here", hint: "or click to browse — PNG, JPG, WEBP, GIF", formats: ["PNG", "JPG", "WEBP", "GIF"], note: "Converted right in your browser — never stored" },
  base64: { title: "Drop your image here", hint: "or click to browse — PNG, JPG, WEBP, GIF, SVG", formats: ["PNG", "JPG", "WEBP", "GIF", "SVG"], note: "Encoded right in your browser — never stored" },
};
const head = computed(() => (isAscii.value ? HEAD.ascii : HEAD.base64));

/* ---------- ASCII 状态 ---------- */
const RAMPS = [
  { label: "Classic", set: " .:-=+*#%@" },
  { label: "Fine", set: " .,:;i1tfLCG08@" },
  { label: "Blocks", set: " ░▒▓█" },
];
const cols = ref(120);
const rampIdx = ref(0);
const invert = ref(true);
const asciiText = ref("");
const asciiDims = ref("—");
let asciiImg = null; // 已加载的 Image，供重渲染

/* ---------- Base64 状态 ---------- */
const B64_MODES = [
  { mode: "datauri", label: "Data URI" },
  { mode: "raw", label: "Raw Base64" },
  { mode: "css", label: "CSS background" },
];
const b64Mode = ref("datauri");
const dataUri = ref("");

function fmtBytes(b) {
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(2) + " MB";
}

/* ---------- ASCII 渲染（复刻 render()）---------- */
function renderAscii() {
  if (!asciiImg) return;
  const w = +cols.value;
  const ar = asciiImg.naturalHeight / asciiImg.naturalWidth;
  const h = Math.max(1, Math.round(w * ar * 0.5));
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const cx = c.getContext("2d");
  cx.drawImage(asciiImg, 0, 0, w, h);
  let d;
  try { d = cx.getImageData(0, 0, w, h).data; } catch (e) { asciiText.value = "Preview not available for this image."; return; }
  const ramp = RAMPS[rampIdx.value].set;
  let s = "";
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      let l = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) / 255;
      if (invert.value) l = 1 - l;
      s += ramp[Math.min(ramp.length - 1, Math.floor(l * (ramp.length - 1)))];
    }
    s += "\n";
  }
  asciiText.value = s;
  asciiDims.value = w + " × " + h + " chars";
}

/* ---------- Base64 输出（复刻 renderOut()）---------- */
const b64Output = computed(() => {
  if (!dataUri.value) return "";
  const raw = dataUri.value.split(",")[1] || "";
  if (b64Mode.value === "raw") return raw;
  if (b64Mode.value === "css") return 'background-image: url("' + dataUri.value + '");';
  return dataUri.value;
});
const origSizeLabel = computed(() => (fileSize.value ? fmtBytes(fileSize.value) : "—"));
const b64SizeLabel = computed(() => (dataUri.value ? fmtBytes(dataUri.value.length) : "—"));

/* ascii 控件变动 → 实时重渲染 */
watch([cols, rampIdx, invert], renderAscii);

function revoke() { if (lastUrl) { URL.revokeObjectURL(lastUrl); lastUrl = ""; } }

function loadFile(file) {
  if (!file || !file.type.startsWith("image/")) { alert("Please choose an image file."); return; }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  fileName.value = file.name;
  fileSize.value = file.size;
  revoke();
  const url = URL.createObjectURL(file);
  lastUrl = url;
  thumbUrl.value = url;

  if (isAscii.value) {
    const img = new Image();
    img.onload = async () => {
      asciiImg = img;
      stage.value = "work";
      await nextTick();
      renderAscii();
    };
    img.onerror = () => { alert("Could not load this image."); };
    img.src = url;
  } else {
    const r = new FileReader();
    r.onload = () => { dataUri.value = r.result; stage.value = "work"; };
    r.readAsDataURL(file);
  }
}

function pick() { fileInput.value && fileInput.value.click(); }
function onInput(e) { const f = e.target.files && e.target.files[0]; if (f) loadFile(f); }
function onDrop(e) { e.preventDefault(); dragging.value = false; const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) loadFile(f); }

function reset() {
  stage.value = "drop";
  revoke();
  thumbUrl.value = "";
  asciiImg = null;
  asciiText.value = "";
  asciiDims.value = "—";
  dataUri.value = "";
  if (fileInput.value) fileInput.value.value = "";
}

const copyText = computed(() => (isAscii.value ? asciiText.value : b64Output.value));
async function copy() {
  try { if (navigator.clipboard) await navigator.clipboard.writeText(copyText.value); } catch (_) {}
  copied.value = true;
  setTimeout(() => (copied.value = false), 1500);
}
function downloadTxt() {
  const b = new Blob([asciiText.value], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(b);
  a.download = "ascii-art.txt";
  a.click();
}

onBeforeUnmount(revoke);
</script>

<template>
  <div class="enc">
    <input ref="fileInput" type="file" accept="image/*" hidden @change="onInput" />

    <!-- 拖放区 -->
    <div v-show="stage === 'drop'" class="dz" :class="{ drag: dragging }"
      @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false" @drop="onDrop">
      <div class="dz-ico">
        <svg v-if="isAscii" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M4 12h10M4 17h16" /></svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M8 9l-3 3 3 3M16 9l3 3-3 3M13 7l-2 10" /></svg>
      </div>
      <h3>{{ head.title }}</h3>
      <div class="hint">{{ head.hint }}</div>
      <div class="formats"><span v-for="f in head.formats" :key="f">{{ f }}</span></div>
      <div class="dz-or">{{ head.note }}</div>
      <button class="btn btn-amber" type="button" @click.stop="pick">Choose Image</button>
    </div>

    <!-- 工作区 -->
    <div v-if="stage === 'work'" class="ws">
      <div class="ws-grid">
        <!-- 预览：ascii=文本块；base64=图片预览 -->
        <div class="ws-preview" :class="{ dark: isAscii }">
          <div v-if="isAscii" class="ascii-out"><pre>{{ asciiText }}</pre></div>
          <img v-else :src="thumbUrl" class="b64-preview" alt="preview" />
        </div>

        <div class="ws-panel">
          <div class="ws-file">
            <img v-if="thumbUrl" class="thumb" :src="thumbUrl" alt="" />
            <div class="meta">
              <div class="fn">{{ fileName }}</div>
              <div class="fsz">{{ isAscii ? asciiDims : fmtBytes(fileSize) + " · original" }}</div>
            </div>
            <button class="rm" type="button" title="Remove" @click="reset"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></button>
          </div>

          <!-- ASCII 控件 -->
          <template v-if="isAscii">
            <div class="ctl">
              <label>Detail (width) <span class="hl-v">{{ cols }}</span> chars</label>
              <div class="rangewrap"><input v-model.number="cols" type="range" min="40" max="200" /></div>
            </div>
            <div class="ctl">
              <label>Character set</label>
              <div class="seg">
                <button v-for="(r, i) in RAMPS" :key="r.label" type="button" :class="{ active: rampIdx === i }" @click="rampIdx = i">{{ r.label }}</button>
              </div>
            </div>
            <div class="toggle-row">
              <span class="tlabel">Invert (dark background)</span>
              <button type="button" class="switch" :class="{ on: invert }" @click="invert = !invert"></button>
            </div>
            <div class="ws-actions">
              <button class="btn btn-amber btn-lg" type="button" @click="copy">{{ copied ? "Copied!" : "Copy text" }}</button>
              <button class="btn btn-outline" type="button" @click="downloadTxt">Download .txt</button>
              <button class="btn btn-ghost" type="button" @click="reset">Convert another</button>
            </div>
          </template>

          <!-- Base64 控件 -->
          <template v-else>
            <div class="ctl">
              <label>Output format</label>
              <div class="seg">
                <button v-for="m in B64_MODES" :key="m.mode" type="button" :class="{ active: b64Mode === m.mode }" @click="b64Mode = m.mode">{{ m.label }}</button>
              </div>
            </div>
            <div class="ctl">
              <label>Base64 output</label>
              <textarea class="b64-out" :value="b64Output" readonly spellcheck="false"></textarea>
            </div>
            <div class="b64-meta">
              <span>Original: <b>{{ origSizeLabel }}</b></span>
              <span>Base64: <b>{{ b64SizeLabel }}</b></span>
            </div>
            <div class="ws-actions">
              <button class="btn btn-amber btn-lg" type="button" @click="copy">{{ copied ? "Copied!" : "Copy to clipboard" }}</button>
              <button class="btn btn-outline" type="button" @click="reset">Encode another</button>
            </div>
          </template>
        </div>
      </div>

      <div class="trust">
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Private — files stay on your device</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>{{ isAscii ? "Live preview" : "Instant encoding" }}</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.enc { width: 100%; }
.dz { border: 2px dashed var(--line-strong); border-radius: 20px; background: var(--surface); padding: 48px 24px; text-align: center; cursor: pointer; transition: 0.18s; }
.dz.drag, .dz:hover { border-color: #f59e0b; background: linear-gradient(180deg, color-mix(in oklab, #f59e0b 8%, var(--surface)), var(--surface-2)); }
.dz-ico { width: 56px; height: 56px; margin: 0 auto 14px; color: #f59e0b; }
.dz-ico svg { width: 100%; height: 100%; }
.dz h3 { font-size: 20px; font-weight: 700; }
.dz .hint { color: var(--tx-dim); font-size: 14px; margin-top: 6px; }
.dz .formats { margin: 12px 0; display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
.dz .formats span { font-size: 12px; font-weight: 700; color: var(--tx-faint); border: 1px solid var(--line); border-radius: 7px; padding: 3px 10px; }
.dz-or { color: var(--tx-faint); font-size: 13px; margin: 10px 0 16px; }
.btn-amber { background: linear-gradient(110deg, #f59e0b, #f97316); color: #1a1205; border: none; font-weight: 800; }
.btn-amber:hover { filter: brightness(1.06); }
.btn-ghost { background: none; border: none; color: var(--tx-dim); cursor: pointer; }

.ws-grid { display: grid; grid-template-columns: 1fr 360px; gap: 22px; align-items: start; }
.ws-preview { min-width: 0; border: 1px solid var(--line); border-radius: 16px; overflow: hidden; display: grid; place-items: center; min-height: 320px; background: var(--surface-2); padding: 14px; }
.ws-preview.dark { background: #06070b; }
.b64-preview { max-width: 100%; max-height: 460px; height: auto; border-radius: 10px; display: block; }
.ascii-out { width: 100%; max-height: 420px; overflow: auto; background: #06070b; border-radius: 11px; }
.ascii-out pre { font-family: ui-monospace, Menlo, monospace; font-size: 5px; line-height: 5px; color: #cdd3df; white-space: pre; margin: 0; letter-spacing: 0; }

.ws-panel { display: flex; flex-direction: column; gap: 16px; }
.ws-file { display: flex; align-items: center; gap: 10px; border: 1px solid var(--line); border-radius: 12px; padding: 10px 12px; background: var(--surface); }
.ws-file .thumb { width: 42px; height: 42px; object-fit: cover; border-radius: 9px; border: 1px solid var(--line); flex: none; }
.ws-file .meta { flex: 1; min-width: 0; }
.ws-file .fn { font-weight: 600; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ws-file .fsz { font-size: 12px; color: var(--tx-faint); }
.ws-file .rm { background: none; border: none; color: var(--tx-faint); cursor: pointer; flex: none; }

.ctl label { font-size: 12px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--tx-faint); display: block; margin-bottom: 10px; }
.hl-v { color: #f4cd76; }
.rangewrap { display: flex; align-items: center; }
.rangewrap input[type="range"] { width: 100%; accent-color: #f59e0b; }
.seg { display: flex; gap: 6px; flex-wrap: wrap; }
.seg button { flex: 1; min-width: 78px; padding: 10px; border-radius: 10px; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx-dim); font-family: var(--font-b); font-weight: 700; font-size: 13px; cursor: pointer; transition: 0.15s; }
.seg button.active { background: color-mix(in oklab, #f59e0b 16%, transparent); border-color: #f59e0b; color: #f4cd76; }

.toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.toggle-row .tlabel { font-weight: 700; color: var(--tx-dim); font-size: 13px; }
.switch { width: 42px; height: 24px; border-radius: 999px; background: var(--surface-2); border: 1px solid var(--line); position: relative; cursor: pointer; padding: 0; transition: 0.18s; flex: none; }
.switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: var(--tx-faint); transition: 0.18s; }
.switch.on { background: #f59e0b; border-color: #f59e0b; }
.switch.on::after { left: 20px; background: #1a1205; }

.b64-out { width: 100%; min-height: 150px; resize: vertical; background: var(--surface-2); border: 1px solid var(--line); border-radius: 11px; padding: 13px 14px; color: var(--tx); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; font-size: 12px; line-height: 1.5; outline: none; word-break: break-all; }
.b64-out:focus { border-color: #f59e0b; }
.b64-meta { display: flex; gap: 20px; font-size: 12.5px; color: var(--tx-faint); font-weight: 600; }
.b64-meta b { color: var(--tx); font-family: var(--font-h); }

.ws-actions { display: flex; flex-direction: column; gap: 10px; }

.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 20px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: #f59e0b; }
@media (max-width: 760px) { .ws-grid { grid-template-columns: 1fr; } }
</style>
